import { EmptyState, FormItem, NoDataIcon, Select } from '@lmc/ui-kit'
import { Button, Text } from '@lmc/ui-kit/src/general'
import { InputNumber } from 'antd'
import type { DefaultOptionType } from 'antd/es/cascader'
import type { FC } from 'react'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'
import styled from 'styled-components'
import { DeleteButton } from '../components'
import { getFilteredFundingTypePatternOptions, getRequiredRule } from '../utils'
import InputSuffix from './InputSuffix'
import { cleanDecimalValues, getMaxValueValidator } from './utils'

type SkillOption = {
  value: string
  label: string
}

export const SkillsRequiredRowInput: FC<{
  skillOptions?: DefaultOptionType[]
  skill?: SkillOption
  name: number
  onRemove?: () => void
  maxValue?: number
}> = ({ skillOptions, onRemove, name, maxValue }) => {
  const { t } = useTranslation()

  return (
    <FormItem
      shouldUpdate={(prevState, currentState) =>
        prevState?.skills[name]?.skillId !== currentState?.skills[name]?.skillId
      }
    >
      {({ getFieldValue }) => {
        return (
          <StyledSection>
            <FormItem
              name={[name, 'skillId']}
              rules={[getRequiredRule('skill')]}
            >
              <Select
                options={getFilteredFundingTypePatternOptions(
                  skillOptions || [],
                  getFieldValue('skills')
                )}
                placeholder="Select a skill"
                style={{ width: 240 }}
                notFoundContent={
                  <EmptyState
                    containerStyle={{ paddingTop: 24 }}
                    title={
                      <EmptyStateWrapper>
                        <Text>{t('SELECT_EMPTY_STATE_SKILLS')}</Text>
                      </EmptyStateWrapper>
                    }
                    description={
                      <Link
                        to="/settings/skills-and-needs"
                        target="_blank"
                        rel="noreferrer"
                      >
                        <Button variant="primary">{t('ADD')}</Button>
                      </Link>
                    }
                    icon={
                      <NoDataIconWrapper>
                        <NoDataIcon />
                      </NoDataIconWrapper>
                    }
                  />
                }
              />
            </FormItem>
            <FormItem
              name={[name, 'skillSupplyNumber']}
              rules={[
                getRequiredRule('team member'),
                {
                  validator: (props, value: string) =>
                    getMaxValueValidator(
                      value,
                      t('MAX_VALUE_VALIDATION_ERROR', { value: maxValue }),
                      maxValue
                    )
                }
              ]}
            >
              <InputNumber
                suffix={
                  <InputSuffix>
                    {t('TEAM_MEMBERS').toLocaleLowerCase()}
                  </InputSuffix>
                }
                min={1}
                max={maxValue}
                style={{ width: 172 }}
                type="number"
                formatter={cleanDecimalValues}
              />
            </FormItem>
            <DeleteButton onClick={() => onRemove?.()} />
          </StyledSection>
        )
      }}
    </FormItem>
  )
}

const StyledSection = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 8px;
  width: 450px;
`

const EmptyStateWrapper = styled.div`
  display: flex;
  flex-direction: column;
`

const NoDataIconWrapper = styled.div`
  scale: 0.5;
  height: 50px;
  display: flex;
  align-items: center;
`
